'use client'

import { useState, useEffect, useCallback } from 'react'
import { X } from 'lucide-react'

const NOTION_FORM_URL =
  'https://wax-waxflower-357.notion.site/ebd/37c512e88e908176be1fefe54d484f69'

interface ApplyModalProps {
  label?: string
}

export default function ApplyModal({ label = 'Apply Now' }: ApplyModalProps) {
  const [open, setOpen] = useState(false)

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close()
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, close])

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center w-full gap-2 px-6 py-3 rounded-xl bg-brand-green text-white text-sm font-semibold hover:bg-brand-green transition-colors"
      >
        {label}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Application Form"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={close}
            aria-hidden="true"
          />

          {/* Modal panel */}
          <div className="relative z-10 w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-brand-soft-gray">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-green-soft flex items-center justify-center">
                  <span className="text-brand-green text-sm font-bold">N</span>
                </div>
                <span className="font-semibold text-brand-charcoal text-sm">Application Form</span>
              </div>
              <button
                onClick={close}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-brand-muted hover:text-brand-charcoal-75 hover:bg-brand-soft-gray transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Iframe */}
            <div className="flex-1 overflow-hidden">
              <iframe
                src={NOTION_FORM_URL}
                width="100%"
                height="600"
                frameBorder="0"
                allowFullScreen
                className="w-full h-[600px] block"
                title="Application Form"
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}
